import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AuthPageToggleBar from "../components/AuthPageToggleBar";
import { useTheme } from "../contexts/ThemeContext";
import { useSystemButtonStyles } from "../hooks/useSystemButtonStyles";
import { auth } from "../supabase/client";
import { completeMemberProfile } from "../utils/api/api";
import InputValidator from "../utils/security/inputValidator";
import { isPersonnummerPlausible } from "../utils/swedishPersonnummer";
import { compressImage } from "../utils/utils/imageUtils";
import { uploadUserProfile } from "../utils/utils/supabaseStorage";
import newEraLogo from "../NewEraLogo.png";
import { getMm } from "../theme/modernMaterialEnterprise";

const LOGO_LIGHT = `${process.env.PUBLIC_URL || ""}/NewEraBlackLogo.png`;
const LOGO_DARK = newEraLogo;

/**
 * Mandatory profile completion for prova-på (Anmälningar) members on first login.
 */
export default function CompleteProfileScreen({ userInfo, profileCompletion, onCompleted }) {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { primaryButtonSx } = useSystemButtonStyles();
  const isMobile = useMediaQuery("(max-width:600px)");
  const user = userInfo?.[0];

  const [address, setAddress] = useState(user?.address || "");
  const [personnummer, setPersonnummer] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const cardBorder = `1px solid ${getMm(isDarkMode).outlineVariant}`;
  const text = getMm(isDarkMode).onSurface;
  const muted = getMm(isDarkMode).onSurfaceVariant;

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!user) return;
    setError(null);

    if (!address.trim()) {
      setError(t("profile_completion_address_required"));
      return;
    }
    if (!personnummer.trim()) {
      setError(t("profile_completion_personnummer_required"));
      return;
    }
    if (!isPersonnummerPlausible(personnummer)) {
      setError(t("profile_completion_personnummer_invalid"));
      return;
    }
    if (!imageFile && !user.image) {
      setError(t("profile_completion_image_required"));
      return;
    }
    if (!password.trim()) {
      setError(t("profile_completion_password_required"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("change_password_mismatch"));
      return;
    }
    const passwordValidation = InputValidator.validateNewMemberPassword(password);
    if (!passwordValidation.isValid) {
      const code = passwordValidation.errorCode || "invalid";
      setError(t(`profile_completion_password_${code}`, passwordValidation.error));
      return;
    }

    setSubmitting(true);
    try {
      const { error: passwordError } = await auth.updateUser({ password: password.trim() });
      if (passwordError) {
        throw new Error(passwordError.message || t("profile_completion_password_failed"));
      }

      let imageUrl = null;
      if (imageFile) {
        const compressedImage = await compressImage(imageFile);
        const compressedFile = new File([compressedImage], imageFile.name, { type: imageFile.type });
        const reader = new FileReader();
        const base64Image = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(compressedFile);
        });
        const base64String = String(base64Image).split(",")[1];
        const uploadResult = await uploadUserProfile(
          base64String,
          user.studentID,
          compressedFile.type
        );
        if (!uploadResult?.success) {
          throw new Error(uploadResult?.error || t("profile_completion_upload_failed"));
        }
        imageUrl = uploadResult.data.publicUrl;
      }

      const result = await completeMemberProfile(
        {
          studentID: user.studentID,
          address: address.trim(),
          personnummer: personnummer.trim(),
          imageUrl,
        },
        user.clubID ?? user.clubId
      );

      if (typeof onCompleted === "function") {
        await onCompleted(result);
      }

      if (
        result?.membershipAccess === "payment_required" &&
        result?.paymentRedirectUrl
      ) {
        window.location.assign(result.paymentRedirectUrl);
        return;
      }
    } catch (err) {
      const apiCode = err?.response?.data?.code;
      if (apiCode === "personnummer" || apiCode === "age" || apiCode === "checksum" || apiCode === "format") {
        setError(err?.response?.data?.error || t("profile_completion_personnummer_invalid"));
      } else {
        setError(err?.response?.data?.error || err.message || t("profile_completion_save_failed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
        bgcolor: getMm(isDarkMode).surfaceLow,
        position: "relative",
      }}
    >
      <AuthPageToggleBar showOnlyOnLogin={false} />
      <Box sx={{ width: "100%", maxWidth: 520 }}>
        <Box sx={{ textAlign: "center", mb: { xs: 2.5, sm: 3 } }}>
          <Box
            component="img"
            src={isDarkMode ? LOGO_DARK : LOGO_LIGHT}
            alt="NewEra"
            sx={{
              maxWidth: isMobile ? 200 : 240,
              width: "100%",
              height: "auto",
              display: "block",
              mx: "auto",
            }}
          />
        </Box>

      <Paper
        elevation={0}
        sx={{
          width: "100%",
          p: { xs: 3, sm: 4 },
          borderRadius: "16px",
          border: cardBorder,
          bgcolor: getMm(isDarkMode).surfaceLowest,
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1.375rem", color: text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {t("profile_completion_title")}
          </Typography>
          {user?.name ? (
            <Typography sx={{ color: muted, fontSize: "0.9375rem", fontFamily: "'Plus Jakarta Sans', sans-serif", mt: 0.25 }}>
              {user.name}
            </Typography>
          ) : null}
        </Box>

        <Typography sx={{ color: muted, mb: 3, lineHeight: 1.6, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {t("profile_completion_body")}
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
          <Avatar
            src={imagePreview || undefined}
            sx={{ width: 96, height: 96, mb: 1.5, bgcolor: getMm(isDarkMode).outlineVariant }}
          />
          <Button component="label" startIcon={<PhotoCameraIcon />} variant="outlined" size="small">
            {t("upload_profile_image")}
            <input hidden accept="image/*" type="file" onChange={handleImageChange} />
          </Button>
        </Box>

        <TextField
          fullWidth
          label={t("address")}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          sx={{ mb: 2 }}
          required
        />

        <TextField
          fullWidth
          label={t("personnummer")}
          value={personnummer}
          onChange={(e) => setPersonnummer(e.target.value)}
          autoComplete="off"
          required
          sx={{ mb: 2 }}
          helperText={t("personnummer_helper")}
          error={personnummer.trim() !== "" && !isPersonnummerPlausible(personnummer)}
        />

        <Typography
          sx={{
            color: text,
            fontWeight: 700,
            fontSize: "0.9375rem",
            mb: 1.5,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {t("profile_completion_password_section")}
        </Typography>
        <Typography sx={{ color: muted, mb: 2, fontSize: "0.8125rem", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {t("profile_completion_password_hint")}
        </Typography>

        <TextField
          fullWidth
          type={showPassword ? "text" : "password"}
          label={t("change_password_new")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" aria-label="toggle password">
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          type={showConfirmPassword ? "text" : "password"}
          label={t("change_password_confirm")}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          sx={{ mb: 3 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirmPassword((v) => !v)} edge="end" aria-label="toggle confirm password">
                  {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {error ? (
          <Typography sx={{ color: getMm(isDarkMode).error, mb: 2, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {error}
          </Typography>
        ) : null}

        <Button
          fullWidth
          variant="contained"
          disabled={submitting}
          onClick={handleSubmit}
          sx={primaryButtonSx}
        >
          {submitting ? t("saving") : t("profile_completion_save")}
        </Button>
      </Paper>
      </Box>
    </Box>
  );
}
